import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { ChatConversationSummaryDto } from 'src/api/FiretrolApiClient';
import {
  useCreateChatConversation,
  useDeleteChatConversation,
  useForkChatConversation,
  useRenameChatConversation,
} from 'src/api/queries/chat/useChatConversationMutations';
import {
  useConversationSummaryQuery,
  useConversationsQuery,
} from 'src/api/queries/chat/useChatConversationQueries';
import { queryKeys } from 'src/api/queries/queryKeys';
import { useJobs } from 'src/hooks/useJobs';
import { getErrorMessage, logger, showErrorToast } from 'src/utils';

export interface INewConversationOptions {
  /** The new conversation neither reads nor saves memory notes; fixed once it is created. */
  memoryOff?: boolean;
}

/** Text for the composer to take over; a new nonce re-applies the same text. */
export interface IComposerDraft {
  text: string;
  nonce: number;
}

// Mirrors the server-side title constraints so an invalid rename never leaves
// the client and roundtrips into a 400.
export const MAX_CONVERSATION_TITLE_LENGTH = 255;

export interface IChatConversationLifecycle {
  conversationId: string | null;
  /** The current app path; new conversations and sends carry it as page context. */
  page: string;
  jobId: number | null;
  conversations: ChatConversationSummaryDto[];
  loadingConversations: boolean;
  memoryOff: boolean;
  forkingMessageId: string | null;
  composerDraft: IComposerDraft | null;
  consumeComposerDraft: (nonce: number) => void;
  ensureConversation: () => Promise<string>;
  isForkPending: () => boolean;
  startNewConversation: (options?: INewConversationOptions) => void;
  openConversation: (id: string) => void;
  deleteConversationOnServer: (id: string) => Promise<unknown>;
  renameConversationTitle: (id: string, title: string) => Promise<void>;
  /** Forks at a user message; `onForked` runs just before the view moves to the fork, and only while this fork is still the one the view waits for. */
  forkConversation: (
    messageId: string,
    draftText: string,
    onForked: () => void,
  ) => Promise<void>;
  refreshConversations: () => Promise<void>;
}

/** @registry Which chat conversation is open and the operations on the conversation list: lazy create, select, new chat, delete, rename, fork, and the memory-off choice; used by `ChatProvider`. */
export const useChatConversationLifecycle = (
  drawerOpen: boolean,
): IChatConversationLifecycle => {
  const location = useLocation();
  const { selectedJob } = useJobs();
  const selectedJobId = selectedJob?.id;
  const queryClient = useQueryClient();

  const [conversationId, setConversationId] = useState<string | null>(null);
  const [composerDraft, setComposerDraft] = useState<IComposerDraft | null>(
    null,
  );
  const [forkingMessageId, setForkingMessageId] = useState<string | null>(null);
  // The memory choice for the conversation the next send creates. It also
  // covers the gap between that create and the list refetch that carries it.
  const [pendingMemoryOff, setPendingMemoryOff] = useState(false);
  const draftNonceRef = useRef(0);
  // The fork this view still waits for. Switching conversation clears it, so
  // a fork that resolves later only seeds the cache and the list.
  const pendingForkRef = useRef<symbol | null>(null);

  const conversationsQuery = useConversationsQuery(drawerOpen);
  const summaryQuery = useConversationSummaryQuery(conversationId);
  const { mutateAsync: createConversationAsync } = useCreateChatConversation();
  const { mutateAsync: deleteConversationOnServer } =
    useDeleteChatConversation();
  const { mutateAsync: renameConversationAsync } = useRenameChatConversation();
  const { mutateAsync: forkConversationAsync } = useForkChatConversation();

  const conversations = conversationsQuery.data ?? [];
  const memoryOff =
    (conversationId ? summaryQuery.data?.memoryOff : undefined) ??
    conversations.find((c) => c.id === conversationId)?.memoryOff ??
    pendingMemoryOff;

  const refreshConversations = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.chat.conversations(),
    });
  }, [queryClient]);

  const isForkPending = useCallback(() => pendingForkRef.current !== null, []);

  const releasePendingFork = useCallback(() => {
    pendingForkRef.current = null;
    setForkingMessageId(null);
  }, []);

  const startNewConversation = useCallback(
    (options?: INewConversationOptions) => {
      releasePendingFork();
      setPendingMemoryOff(!!options?.memoryOff);
      setConversationId(null);
    },
    [releasePendingFork],
  );

  const openConversation = useCallback(
    (id: string) => {
      releasePendingFork();
      setPendingMemoryOff(false);
      setConversationId(id);
    },
    [releasePendingFork],
  );

  const consumeComposerDraft = useCallback((nonce: number) => {
    setComposerDraft((current) => (current?.nonce === nonce ? null : current));
  }, []);

  const renameConversationTitle = useCallback(
    async (id: string, title: string) => {
      const trimmed = title.trim();
      if (!trimmed || trimmed.length > MAX_CONVERSATION_TITLE_LENGTH) return;
      const current = queryClient.getQueryData<ChatConversationSummaryDto[]>(
        queryKeys.chat.conversations(),
      );
      if (current?.find((c) => c.id === id)?.title === trimmed) return;
      await renameConversationAsync({ id, title: trimmed }).catch(
        () => undefined,
      );
    },
    [queryClient, renameConversationAsync],
  );

  const ensureConversation = useCallback(async (): Promise<string> => {
    if (conversationId) return conversationId;
    const created = await createConversationAsync({
      jobId: selectedJobId,
      page: location.pathname,
      memoryOff: pendingMemoryOff,
    });
    setConversationId(created.id);
    return created.id;
  }, [
    conversationId,
    selectedJobId,
    location.pathname,
    pendingMemoryOff,
    createConversationAsync,
  ]);

  const forkConversation = useCallback(
    async (messageId: string, draftText: string, onForked: () => void) => {
      if (!conversationId || pendingForkRef.current) return;
      const fork = Symbol('fork');
      pendingForkRef.current = fork;
      setForkingMessageId(messageId);
      try {
        const forked = await forkConversationAsync({
          conversationId,
          messageId,
        });
        if (pendingForkRef.current !== fork) return;
        onForked();
        setPendingMemoryOff(false);
        setConversationId(forked.id);
        draftNonceRef.current += 1;
        setComposerDraft({ text: draftText, nonce: draftNonceRef.current });
      } catch (err) {
        logger.error('Failed to fork chat conversation', { err });
        showErrorToast(getErrorMessage(err));
      } finally {
        if (pendingForkRef.current === fork) releasePendingFork();
      }
    },
    [conversationId, forkConversationAsync, releasePendingFork],
  );

  return {
    conversationId,
    page: location.pathname,
    jobId: selectedJobId ?? null,
    conversations,
    loadingConversations: conversationsQuery.isLoading,
    memoryOff,
    forkingMessageId,
    composerDraft,
    consumeComposerDraft,
    ensureConversation,
    isForkPending,
    startNewConversation,
    openConversation,
    deleteConversationOnServer,
    renameConversationTitle,
    forkConversation,
    refreshConversations,
  };
};
